import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { register, setError } from '../app/redux/slices/authSlice';
import { useNavigate, Link } from 'react-router-dom';
import { RootState, AppDispatch } from '../app/redux/store';
import validator from 'validator';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../app/components/LanguageSwitcher';
import { SERVER_REGION } from '../i18n';

export default function RegisterPage() {
  const { t } = useTranslation(['auth', 'common']);
  const [formData, setFormData] = useState({
    regKey: '',
    email: '',
    company: '',
    password: '',
    confirmPassword: '',
  });
  const [fakeSubmitting, setFakeSubmitting] = useState(false);
  const [keyError, setKeyError] = useState(false);
  const [validEmail, setValidEmail] = useState(true);
  const [validCompany, setValidCompany] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { error, loading } = useSelector((state: RootState) => state.auth);

  // Registration key format validation - only numbers
  useEffect(() => {
    if (formData.regKey) {
      const reg = /^[0-9]+$/;
      if (!reg.test(formData.regKey)) {
        setKeyError(true);
      } else {
        setKeyError(false);
      }
    } else {
      setKeyError(false);
    }
  }, [formData.regKey]);

  // Email validation
  useEffect(() => {
    if (formData.email) {
      if (validator.isEmail(formData.email)) {
        setValidEmail(true);
      } else {
        setValidEmail(false);
      }
    } else {
      setValidEmail(true);
    }
  }, [formData.email]);

  // Company validation
  useEffect(() => {
    if (formData.company && formData.company.trim() !== '') {
      setValidCompany(true);
    } else {
      setValidCompany(false);
    }
  }, [formData.company]);

  // Password validation - at least 8 characters
  useEffect(() => {
    if (formData.password) {
      const reg = /.{8,}$/;
      if (!reg.test(formData.password)) {
        setPasswordError(true);
      } else {
        setPasswordError(false);
      }
    } else {
      setPasswordError(false);
    }
  }, [formData.password]);

  // Password confirmation validation
  useEffect(() => {
    if (formData.confirmPassword && formData.password) {
      if (formData.confirmPassword !== formData.password) {
        setPasswordMismatch(true);
      } else {
        setPasswordMismatch(false);
      }
    } else {
      setPasswordMismatch(false);
    }
  }, [formData.confirmPassword, formData.password]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear any previous errors and reset fake submitting
    dispatch(setError(''));
    setFakeSubmitting(false);

    // Check if all validations pass
    const allFieldsValid = 
      formData.regKey && 
      !keyError &&
      formData.email && 
      validEmail && 
      formData.company &&
      validCompany &&
      formData.password && 
      !passwordError && 
      formData.confirmPassword && 
      !passwordMismatch;


    if (!allFieldsValid) {
      setFakeSubmitting(true);
      // Don't set error message here - let individual field validations show
      return;
    }

    setFakeSubmitting(false);
    
    const registerData = {
      username: formData.email,
      key: formData.regKey,
      email: formData.email,
      company: formData.company,
      password: formData.password,
    };

    try {
      await dispatch(register(registerData)).unwrap();
      
      // Different flow based on region
      if (SERVER_REGION === 'CN') {
        // Show success modal for China - user manually goes to login
        setShowSuccessModal(true);
      } else {
        // Auto-navigate to verification page for Global
        navigate(`/verification?email=${encodeURIComponent(formData.email)}&password=${encodeURIComponent(formData.password)}`);
      }
    } catch {
      // Error is handled by Redux state
    }
  };

  const clearError = () => {
    dispatch(setError(''));
  };

  return (
    <>
      {/* Success Modal for China Region */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{t('auth:registrationSuccessful')}</h3>
              <p className="text-gray-900 mb-6">{t('auth:registrationSuccessMessage')}</p>
              <button 
                onClick={() => navigate('/login')}
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {t('auth:goToLogin')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {/* Logo and Brand */}
          <div className="flex justify-center items-center space-x-4 mb-6">
            <img 
              src="/icon.png" 
              alt="SmartProbe Logo" 
              width={48} 
              height={48} 
              className="w-12 h-12"
            />
            <h1 className="text-2xl font-bold text-gray-900">SmartProbe</h1>
          </div>
          
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            {t('auth:registerTitle')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-900">
            {t('auth:or')}{' '}
            <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
              {t('auth:signInToExisting')}
            </Link>
          </p>
        </div>
        
        {/* Language Switcher */}
        <div className="flex justify-center">
          <LanguageSwitcher />
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="ml-3">
                  <div className="text-sm text-red-700">
                    {error}
                  </div>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={clearError}
                      className="text-sm bg-red-50 text-red-800 rounded-md hover:bg-red-100 transition-colors duration-200 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-500 cursor-pointer"
                    >
                      {t('auth:dismiss')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Registration Key */}
            <div>
              <label htmlFor="regKey" className="block text-sm font-medium text-gray-900">
                {t('auth:registrationKey')}
              </label>
              <input
                id="regKey"
                name="regKey"
                type="text"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('auth:enterRegistrationKey')}
                value={formData.regKey}
                onChange={handleInputChange}
              />
              {!formData.regKey && fakeSubmitting && (
                <p className="mt-1 text-sm text-red-600">{t('auth:registrationKeyRequired')}</p>
              )}
              {keyError && (
                <p className="mt-1 text-sm text-red-600">{t('auth:registrationKeyNumbersOnly')}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                {t('auth:emailAddress')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('auth:enterEmailAddress')}
                value={formData.email}
                onChange={handleInputChange}
              />
              {!formData.email && fakeSubmitting && (
                <p className="mt-1 text-sm text-red-600">{t('auth:emailRequired')}</p>
              )}
              {!validEmail && (
                <p className="mt-1 text-sm text-red-600">{t('auth:validEmailRequired')}</p>
              )}
            </div>

            {/* Company */}
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-900">
                {t('auth:company')}
              </label>
              <input
                id="company"
                name="company"
                type="text"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('auth:enterCompanyName')}
                value={formData.company}
                onChange={handleInputChange}
              />
              {!validCompany && fakeSubmitting && (
                <p className="mt-1 text-sm text-red-600">{t('auth:companyRequired')}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                {t('auth:password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('auth:enterPassword')}
                value={formData.password}
                onChange={handleInputChange}
              />
              {!formData.password && fakeSubmitting && (
                <p className="mt-1 text-sm text-red-600">{t('auth:passwordRequired')}</p>
              )}
              {passwordError && (
                <p className="mt-1 text-sm text-red-600">{t('auth:passwordMinLength')}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900">
                {t('auth:confirmPasswordLabel')}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder={t('auth:confirmPasswordPlaceholder')}
                value={formData.confirmPassword}
                onChange={handleInputChange}
              />
              {!formData.confirmPassword && fakeSubmitting && (
                <p className="mt-1 text-sm text-red-600">{t('auth:confirmPasswordRequired')}</p>
              )}
              {passwordMismatch && (
                <p className="mt-1 text-sm text-red-600">{t('auth:passwordsDoNotMatch')}</p>
              )}
            </div>
          </div>

          {/* Field validation summary */}
          <div className="text-sm text-gray-900 space-y-1">
            <p className="font-medium">{t('auth:requiredFields')}</p>
            <div className="grid grid-cols-2 gap-2">
              <span className={`flex items-center ${formData.regKey && !keyError ? 'text-green-600' : 'text-red-500'}`}>
                {formData.regKey && !keyError ? '✓' : '✗'} {t('auth:registrationKey')}
              </span>
              <span className={`flex items-center ${formData.email && validEmail ? 'text-green-600' : 'text-red-500'}`}>
                {formData.email && validEmail ? '✓' : '✗'} {t('auth:emailAddress')}
              </span>
              <span className={`flex items-center ${formData.company && validCompany ? 'text-green-600' : 'text-red-500'}`}>
                {formData.company && validCompany ? '✓' : '✗'} {t('auth:company')}
              </span>
              <span className={`flex items-center ${formData.password && !passwordError ? 'text-green-600' : 'text-red-500'}`}>
                {formData.password && !passwordError ? '✓' : '✗'} {t('auth:password')}
              </span>
              <span className={`flex items-center ${formData.confirmPassword && !passwordMismatch ? 'text-green-600' : 'text-red-500'}`}>
                {formData.confirmPassword && !passwordMismatch ? '✓' : '✗'} {t('auth:confirmPassword')}
              </span>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={
                loading ||
                !formData.regKey ||
                !formData.email ||
                !validEmail ||
                !formData.company ||
                !validCompany ||
                !formData.password ||
                passwordError ||
                !formData.confirmPassword ||
                passwordMismatch ||
                keyError
              }
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                loading ||
                !formData.regKey ||
                !formData.email ||
                !validEmail ||
                !formData.company ||
                !validCompany ||
                !formData.password ||
                passwordError ||
                !formData.confirmPassword ||
                passwordMismatch ||
                keyError
                  ? 'text-gray-400 bg-gray-300 cursor-not-allowed'
                  : 'text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 cursor-pointer'
              }`}
            >
              {loading ? t('auth:creatingAccount') : t('auth:registerButton')}
            </button>
          </div>

          <div className="text-center space-y-2">
            <Link 
              to="/login" 
              className="font-medium text-indigo-600 hover:text-indigo-500 text-sm"
            >
              {t('auth:alreadyHaveAccount')} {t('auth:signInHere')}
            </Link>
            <br />
            <Link 
              to="/continue-verification" 
              className="font-medium text-indigo-600 hover:text-indigo-500 text-sm"
            >
              {t('auth:continueVerification')}
            </Link>
          </div>
        </form>
      </div>
      
      {/* AIVision Food Branding - Bottom Right */}
      <div className="fixed bottom-4 right-4 flex items-center space-x-2 text-gray-900">
        <img 
          src="/icon.png" 
          alt="AIVision Food Logo" 
          width={24} 
          height={24} 
          className="w-6 h-6"
        />
        <span className="text-sm font-medium">AIVision Food</span>
        </div>
      </div>
    </>
  );
}
